/**
 * The composition root (CLAUDE.md working rule #8) — the ONLY place that instantiates a page.
 * Owns the `#app-root` container, resolves the current route + auth state on every change, and
 * mounts exactly one page at a time, disposing the previous one first. The Babylon engine/scene is
 * only ever constructed inside `pages/editor-page.ts`'s `mount()`, so it is loaded for exactly one
 * route and never for the site pages.
 *
 * It used to say here that "the 3D scene structurally cannot load before login". That stopped being
 * true with SCRUM-250: a NEW rider is editable with no account, because putting a registration form
 * in front of the one screen that explains the product was the most expensive thing this app did.
 * The guard below is where that lives, and `/editor?rider=<id>` still requires auth.
 */
import { isLoggedIn, subscribe } from "../auth/auth-service";
import { closeDialogsLeftOpen } from "./close-dialogs";
import { hasGuestDraft } from "../editor/guest-draft";
import { installAccountMoveOnEnter } from "./account-move-on-enter";
import { loadDictionary, localeFromParam } from "../i18n";
import { getStoredUser } from "../api/http";
import { getPendingOfferIntent } from "../offer/offer-return-intent";
import { getCrewReturnIntent } from "../crew/crew-return-intent";
import { loginHeldForRoleStep } from "../auth/role-step";
import { asksForTerms, currentRoute, isPublicRoute, navigate, onRouteChange, type Route } from "./router";
import { ensureTermsAccepted } from "../components/consent-gate";
import { hasCabinetShell } from "./cabinet-shell";

/** Every page's `mount()` returns one of these — `dispose()` runs right before the NEXT page
 *  mounts (or never, if the app is torn down entirely, which doesn't happen in this SPA). Pages
 *  that own external resources (timers, `window` listeners, a Babylon `Engine`) MUST release them
 *  here — `editor-page.ts` is the one page that actually needs this (render loop + resize listener). */
export interface PageHandle {
  dispose(): void;
}

/** Route guard: which routes require auth, and where an unauthenticated hit redirects to.
 *
 *  Exported for `app-shell.test.ts` and for no other reason — this function decides whether a
 *  stranger sees the product or a login form (SCRUM-250), and whether a signed-in visitor with
 *  unsaved work lands back on it. Both are one-line mistakes away from being wrong in a way no
 *  typecheck notices, so the decision table is asserted directly rather than inferred from a
 *  browser run. Nothing else may call it: `startAppShell` below is the only production caller. */
export function resolveEffectiveRoute(route: Route, loggedIn: boolean): Route {
  // A support-issued temporary password (SCRUM-131) closes the app down to one screen. Checked
  // FIRST, ahead of even the public routes: the account is in a state where nothing else should be
  // reachable, and the flag only exists on a signed-in session anyway. The API enforces the same
  // rule on its own side, so this redirect is for the user's benefit — without it they would just
  // meet a 403 on whatever they clicked.
  if (loggedIn && getStoredUser()?.mustChangePassword && route.name !== "change-password") {
    return { name: "change-password" };
  }
  if (route.name === "login") {
    // Just registered on the sign-in form: the role step is on screen there (SCRUM-970, `auth/role-step.ts`).
    if (loggedIn && loginHeldForRoleStep()) return route;
    if (loggedIn) {
      // A signed-out "Make offer" click (PR2, docs/V2_OFFER_TECH_APPROVAL.md) sent them here with
      // the rider it was clicked on remembered — there is no generic "return to where I was" in
      // this router, so this is its one deliberate exception. Checked BEFORE the guest-draft
      // branch below: the two intents are independent (a guest editing a NEW rider and a signed-out
      // visitor answering somebody else's PUBLISHED one are different visitors in practice), and an
      // offer click is the more specific, more recently-expressed intent of the two.
      //
      // This does not itself create the offer or clear the stored intent — `public-viewer-page.ts`'s
      // mount does that once it actually lands here with a fresh token, which is also the only place
      // that can report a failure (own-rider 400, a withdrawn publication, …) to the visitor.
      const offerIntent = getPendingOfferIntent();
      if (offerIntent) {
        return { name: "public-viewer", riderId: offerIntent.riderId, version: offerIntent.version, ...(offerIntent.showToken ? { showToken: offerIntent.showToken } : {}) };
      }
      // A worker who opened a crew task link signed out (SCRUM-762) goes back to that link; the page
      // clears the intent once it lands there signed in.
      const crewIntent = getCrewReturnIntent();
      if (crewIntent) return { name: "crew-task", token: crewIntent };
      // Signing in with work in progress goes BACK to the editor, not to the riders list (SCRUM-250).
      // A guest who pressed Save was sent here by the editor; landing them on a list of riders they
      // do not have yet would read as "my work is gone".
      if (hasGuestDraft()) return { name: "editor", riderId: null };
      // The default landing: a rental company's is its Warehouse page (SCRUM-647). Owner, 19.10.2026: «давай те кто
      // регистрировался через оффер попадают по умолчанию в рентал и те кто выбрал в профиле прокатчик тоже»; the same
      // day the Rental desk moved onto the Warehouse page. One field decides — the profile role; the server sets it to
      // `rental` for an account made on the offer path.
      return getStoredUser()?.userRole === "rental" ? { name: "warehouse" } : { name: "riders" };
    }
    return route; // already logged in -> no reason to see the login form
  }
  // The recovery routes stay reachable in BOTH states. They arrive from an emailed link, and the
  // person clicking it is by definition someone who couldn't get in — bouncing them to /login (or,
  // worse, silently to /riders because an old session is still valid in that browser) would make
  // the link useless exactly when it matters.
  if (isPublicRoute(route)) return route;
  // A NEW rider is editable with no account at all (SCRUM-250): the product explains itself in
  // thirty seconds of 3D, and a registration form in front of that screen is the most expensive
  // thing we had. The catalogue this needs is already anonymous on the server side — `GET /presets`
  // and `/presets/:id` run under `OptionalJwtAuthGuard`, categories and tags are unguarded, and the
  // uploaded `.glb` files are served under a model pass that needs no account — so nothing here widens the API surface.
  //
  // `/editor?rider=<id>` still requires auth, and deliberately: fetching a specific stored rider is
  // an authenticated read, so letting the route through would only mean showing an empty editor and
  // a load error. Sending them to sign in first is the honest answer.
  if (route.name === "editor" && !route.riderId) return route;
  // "riders", "profile" and a rider-specific "editor" require auth.
  return loggedIn ? route : { name: "login" };
}

/** Страницы, у которых общая оболочка кабинета (шапка + левое меню). Гостевой склад сюда НЕ входит:
 *  у него своя шапка и меню нет вовсе, поэтому вход в аккаунт обязан пересобрать страницу. */
function isCabinetRoute(route: Route): boolean {
  switch (route.name) {
    case "riders":
    case "offers":
    case "shows":
    case "show-detail":
    case "profile":
    case "admin":
      return true;
    case "warehouse":
      return isLoggedIn();
    default:
      return false;
  }
}

export function startAppShell(): void {
  const found = document.getElementById("app-root");
  if (!found) throw new Error("app-shell: #app-root not found in index.html");
  const container: HTMLElement = found; // narrowed once, up front — closures below capture this const, not `found`
  // From here on, making an account (or entering one) moves what this browser kept into it at that moment — the warehouse,
  // its «formed» mark and its offer lines (owner, 27.10.2026: «как только человек заводит аккаунт»).
  installAccountMoveOnEnter();

  let activeHandle: PageHandle | undefined;
  let lastMountedKey = "";
  let mountSeq = 0;

  async function mount(route: Route): Promise<void> {
    const key =
      route.name === "editor"
        ? `editor:${route.riderId ?? ""}`
        : route.name === "reset-password" ||
            route.name === "verify-email" ||
            route.name === "verify-email-change" ||
            route.name === "email-login"
          ? // Keyed on the token too: landing on /verify-email with a DIFFERENT token must remount
            // and re-run the redemption, not silently reuse the previous attempt's result.
            `${route.name}:${route.token ?? ""}`
          : route.name === "public-viewer"
            ? // Keyed on id+version+offer, same reasoning as "editor" above. `offerId` matters here:
              // clicking "Make offer" on `/r/:id` (no version) navigates to `/r/:id/v/:n?offer=<id>`
              // — the PATHNAME often changes too (version now pinned), but a rider that was ALREADY
              // opened at that exact pinned version would not, and without `offerId` in the key that
              // click would be a silent no-op remount-wise: the offer overlay would never mount.
              `public-viewer:${route.riderId ?? ""}:${route.version ?? ""}:${route.offerId ?? ""}:${route.showToken ?? ""}`
            : route.name === "show-page" || route.name === "crew-task"
              ? `${route.name}:${route.token}`
              : route.name === "show-detail"
                ? `show-detail:${route.showId}`
                : route.name === "place-card"
                  ? `place-card:${route.placeId}`
                  : route.name === "crew-person"
                    ? `crew-person:${route.contactId}`
                    : route.name;
    if (key === lastMountedKey) return; // avoid a pointless remount (e.g. auth-service firing on an unrelated change)
    lastMountedKey = key;
    const thisSeq = ++mountSeq;

    activeHandle?.dispose();
    activeHandle = undefined;
    // ОБОЛОЧКА КАБИНЕТА ПЕРЕЖИВАЕТ ПЕРЕХОД, остальное стирается как раньше.
    //
    // Владелец, 29.10.2026: «почему при переключени на варехауз левая панель кабинета пропадает на
    // короткое время?» Стирание здесь и было причиной: шапка и меню уезжали вместе с телом, и
    // следующая страница собирала их заново (замер: `#riders-page` снят, через 14 мс добавлен
    // `#my-equipment-page`, и меню в нём уже другой узел). Между двумя страницами кабинета стирать
    // нечего — тело очистит `ensureCabinetShell`, а шапка, меню и баннер остаются те же.
    const keepShell = isCabinetRoute(route) && hasCabinetShell(container);
    if (!keepShell) container.innerHTML = "";
    closeDialogsLeftOpen();

    // Consent gate (docs/V2_POST_CUTOVER_TAILS.md #7): asked once per session on the first
    // authenticated view. Deliberately NOT on public routes — the legal pages themselves must stay
    // readable without an account, and the landing page is marketing, not use of the service.
    // NOT while a temporary password is outstanding (owner report 2026-08-08). The consent gate is
    // a blocking modal whose only action, `POST /users/me/consents/terms`, is refused by
    // `PasswordChangeRequiredGuard` — so the user met "we could not record your agreement" with no
    // way forward: the modal cannot be dismissed and the form behind it cannot be reached. Terms are
    // asked for on the next route after the password is set, which is also the more sensible order:
    // consent belongs to the account's owner, and until the temporary password is replaced we do
    // not know that is who is looking at the screen.
    if (isLoggedIn() && asksForTerms(route) && !getStoredUser()?.mustChangePassword) {
      void ensureTermsAccepted();
    }

    let handle: PageHandle | undefined;

    switch (route.name) {
      case "landing": {
        const { mountLandingPage } = await import("../pages/landing-page");
        if (mountSeq !== thisSeq) return;
        handle = mountLandingPage(container);
        // Warm the editor chunk on INTENT, not on arrival (SCRUM-334). This used to be a bare
        // `void import("../pages/editor-page")`, which pulled @babylonjs/core with it — 1.15 MB over
        // the wire, at modulepreload priority, for every visitor to a page that has no 3D on it.
        const { warmEditorOnIntent } = await import("./warm-editor");
        if (mountSeq !== thisSeq) return;
        const stopWarming = warmEditorOnIntent(container);
        // Wrap rather than replace: the landing page may own resources of its own, and leaving the
        // intent listeners attached after a route change would keep the chunk warming for a page
        // nobody is looking at.
        const mountedHandle = handle;
        handle = {
          dispose: () => {
            stopWarming();
            mountedHandle?.dispose();
          },
        };
        break;
      }
      case "login": {
        const { mountLoginPage } = await import("../pages/login-page");
        if (mountSeq !== thisSeq) return;
        handle = mountLoginPage(container);
        break;
      }
      case "riders": {
        const { mountRidersListPage } = await import("../pages/riders-list-page");
        if (mountSeq !== thisSeq) return;
        handle = mountRidersListPage(container);
        break;
      }
      case "offers": {
        const { mountOffersPage } = await import("../pages/offers-page");
        if (mountSeq !== thisSeq) return;
        handle = mountOffersPage(container);
        break;
      }
      case "shows": {
        const { mountShowsPage } = await import("../pages/shows-page");
        if (mountSeq !== thisSeq) return;
        handle = mountShowsPage(container, route.tab);
        break;
      }
      case "show-detail": {
        const { mountShowDetailPage } = await import("../pages/show-detail-page");
        if (mountSeq !== thisSeq) return;
        handle = mountShowDetailPage(container, route.showId);
        break;
      }
      case "place-card": {
        const { mountPlaceCardPage } = await import("../pages/place-card-page");
        if (mountSeq !== thisSeq) return;
        handle = mountPlaceCardPage(container, route.placeId);
        break;
      }
      case "crew-person": {
        const { mountCrewPersonPage } = await import("../pages/crew-person-page");
        if (mountSeq !== thisSeq) return;
        handle = mountCrewPersonPage(container, route.contactId);
        break;
      }
      case "warehouse": {
        const { mountMyEquipmentPage } = await import("../pages/my-equipment-page");
        if (mountSeq !== thisSeq) return;
        handle = mountMyEquipmentPage(container, route.tab ?? "warehouse", { ...(route.stage ? { stage: route.stage } : {}), ...(route.show ? { show: route.show } : {}) });
        break;
      }
      case "editor": {
        const { mountEditorPage } = await import("../pages/editor-page");
        if (mountSeq !== thisSeq) return;
        handle = mountEditorPage(container, route.riderId);
        break;
      }
      case "profile": {
        const { mountProfilePage } = await import("../pages/profile-page");
        if (mountSeq !== thisSeq) return;
        handle = mountProfilePage(container);
        break;
      }
      case "admin": {
        const { mountAdminPage } = await import("../pages/admin-page");
        if (mountSeq !== thisSeq) return;
        handle = mountAdminPage(container);
        break;
      }
      case "about": {
        const { mountAboutPage } = await import("../pages/about-page");
        if (mountSeq !== thisSeq) return;
        mountAboutPage(container);
        break;
      }
      case "credits": {
        const { mountCreditsPage } = await import("../pages/credits-page");
        if (mountSeq !== thisSeq) return;
        mountCreditsPage(container);
        break;
      }
      case "legal": {
        const { mountLegalPage } = await import("../pages/legal-pages");
        if (mountSeq !== thisSeq) return;
        mountLegalPage(container, route.doc);
        break;
      }
      case "change-password": {
        const { mountForcedPasswordChangePage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountForcedPasswordChangePage(container);
        break;
      }
      case "forgot-password": {
        const { mountForgotPasswordPage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountForgotPasswordPage(container);
        break;
      }
      case "reset-password": {
        const { mountResetPasswordPage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountResetPasswordPage(container, route.token);
        break;
      }
      case "verify-email": {
        const { mountVerifyEmailPage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountVerifyEmailPage(container, route.token);
        break;
      }
      case "email-login": {
        const { mountEmailLoginPage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountEmailLoginPage(container, route.token);
        break;
      }
      case "verify-email-change": {
        const { mountVerifyEmailChangePage } = await import("../pages/recovery-pages");
        if (mountSeq !== thisSeq) return;
        handle = mountVerifyEmailChangePage(container, route.token);
        break;
      }
      case "show-page": {
        const { mountShowPage } = await import("../pages/show-page");
        if (mountSeq !== thisSeq) return;
        handle = mountShowPage(container, route.token);
        break;
      }
      case "crew-task": {
        const { mountCrewPage } = await import("../pages/crew-page");
        if (mountSeq !== thisSeq) return;
        handle = mountCrewPage(container, route.token);
        break;
      }
      case "public-viewer": {
        // ССЫЛКА С `?lang=` ДОЛЖНА ОТКРЫТЬСЯ НА ТОМ ЯЗЫКЕ, который выбрал отправивший
        // (`docs/V2_I18N_PLAN.md` §2), а словари с 29.10.2026 приезжают по требованию (SCRUM-887).
        // Страница собирает разметку СИНХРОННО, и каждая подпись в ней — `t()`, сделанный один раз,
        // поэтому словарь ждут здесь, до монтирования. Не приехал — английский, как и везде.
        const langParam = localeFromParam(new URLSearchParams(window.location.search).get("lang"));
        if (langParam) await loadDictionary(langParam).catch(() => undefined);
        const { mountPublicViewerPage } = await import("../pages/public-viewer-page");
        if (mountSeq !== thisSeq) return;
        handle = mountPublicViewerPage(container, route.riderId, route.version, route.offerId ?? null, route.showToken ?? null);
        break;
      }
    }

    if (mountSeq === thisSeq) {
      activeHandle = handle;
    } else {
      handle?.dispose();
    }
  }

  function resolveAndMount(): void {
    const effective = resolveEffectiveRoute(currentRoute(), isLoggedIn());
    if (effective.name !== currentRoute().name) {
      // `replace: true` — this REWRITES the current entry rather than pushing a new one (SCRUM-250
      // critic finding 4). The route being corrected away from was never actually rendered, so it has
      // no business surviving as its own Back-button stop; `navigate`'s own doc comment has the full
      // story of what pushing here used to do (a permanent Back-button trap on `/login`).
      navigate(effective, { replace: true });
      return; // navigate() dispatches popstate synchronously -> this function re-runs with the corrected route
    }
    void mount(effective);
  }

  onRouteChange(resolveAndMount);
  // Auth state changes (login/register/logout) also need to re-resolve the route even though the
  // URL didn't change — e.g. logging in while sitting on `/login`, or a 401-triggered forced
  // logout while sitting on `/editor`.
  subscribe(resolveAndMount);
}
